import { useState, useEffect } from "react";
import { getPartiteGiacenza, createMovimento, getClienti, getMovimenti, deleteMovimento, updateMovimento, bulkDeleteMovimenti } from "@/lib/api";
import { formatDate, formatNumber, getTodayISO } from "@/lib/utils";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { 
  ArrowUpFromLine, 
  Package, 
  Edit, 
  Trash2,
  Plus,
  Search
} from "lucide-react";
import { toast } from "sonner";
import { motion } from "framer-motion";

export default function Uscite() {
  const [partiteGiacenza, setPartiteGiacenza] = useState([]);
  const [uscite, setUscite] = useState([]);
  const [clienti, setClienti] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [selectedPartita, setSelectedPartita] = useState(null);
  const [editingMovimento, setEditingMovimento] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkDeleteConfirm, setBulkDeleteConfirm] = useState(false);
  
  const [formData, setFormData] = useState({
    cliente: "",
    nuovoCliente: "",
    quantita: "",
    data: getTodayISO(),
    note: "",
  });

  const [editFormData, setEditFormData] = useState({
    quantita: "",
    data: "",
    cliente: "",
    note: "",
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [partiteRes, clientiRes, usciteRes] = await Promise.all([
        getPartiteGiacenza(),
        getClienti(),
        getMovimenti({ tipo: "uscita" }),
      ]);
      setPartiteGiacenza(partiteRes.data);
      setClienti(clientiRes.data);
      setUscite(usciteRes.data);
    } catch (error) {
      console.error("Error loading data:", error);
      toast.error("Errore nel caricamento dei dati");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenUscitaDialog = (partita) => {
    setSelectedPartita(partita);
    setFormData({
      cliente: "",
      nuovoCliente: "",
      quantita: "",
      data: getTodayISO(),
      note: "",
    });
    setDialogOpen(true);
  };

  const handleSubmitUscita = async (e) => {
    e.preventDefault();
    
    const cliente = formData.cliente === "__new__" 
      ? formData.nuovoCliente 
      : formData.cliente;
    
    if (!cliente || !formData.quantita) {
      toast.error("Compila tutti i campi obbligatori");
      return;
    }

    if (parseFloat(formData.quantita) > selectedPartita?.quantita_disponibile) {
      toast.error(`Quantità non disponibile. Disponibile: ${formatNumber(selectedPartita.quantita_disponibile)} kg`);
      return;
    }

    try {
      await createMovimento({
        partita_id: selectedPartita.id,
        tipo: "uscita",
        cliente,
        quantita: parseFloat(formData.quantita),
        data: formData.data,
        note: formData.note,
      });
      
      toast.success("Uscita registrata con successo");
      setDialogOpen(false);
      setSelectedPartita(null);
      loadData();
    } catch (error) {
      console.error("Error creating uscita:", error);
      toast.error(error.response?.data?.detail || "Errore nella registrazione dell'uscita");
    }
  };

  const handleEditUscita = (movimento) => {
    setEditingMovimento(movimento);
    setEditFormData({
      quantita: movimento.quantita.toString(),
      data: movimento.data,
      cliente: movimento.cliente || "",
      note: movimento.note || "",
    });
    setEditDialogOpen(true);
  };

  const handleSubmitEdit = async (e) => {
    e.preventDefault();
    
    if (!editFormData.quantita) {
      toast.error("La quantità è obbligatoria");
      return;
    }

    try {
      await updateMovimento(editingMovimento.id, {
        partita_id: editingMovimento.partita_id,
        tipo: "uscita",
        quantita: parseFloat(editFormData.quantita),
        data: editFormData.data,
        cliente: editFormData.cliente,
        note: editFormData.note,
      });
      
      toast.success("Uscita aggiornata con successo");
      setEditDialogOpen(false);
      setEditingMovimento(null);
      loadData();
    } catch (error) {
      console.error("Error updating uscita:", error);
      toast.error(error.response?.data?.detail || "Errore nell'aggiornamento dell'uscita");
    }
  };

  const handleDeleteUscita = async (id) => {
    try {
      await deleteMovimento(id);
      toast.success("Uscita eliminata con successo");
      setDeleteConfirm(null);
      loadData();
    } catch (error) {
      console.error("Error deleting uscita:", error);
      toast.error("Errore nell'eliminazione dell'uscita");
    }
  };

  const handleBulkDelete = async () => {
    try {
      const response = await bulkDeleteMovimenti(selectedIds);
      toast.success(`Eliminate ${response.data.deleted} uscite`);
      setBulkDeleteConfirm(false);
      setSelectedIds([]);
      loadData();
    } catch (error) {
      console.error("Error bulk deleting:", error);
      toast.error("Errore nell'eliminazione");
    }
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === uscite.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(uscite.map(u => u.id));
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const filteredPartite = partiteGiacenza.filter((p) =>
    p.codice_partita.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.fornitore.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.prodotto.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totaleUscite = uscite.reduce((sum, u) => sum + (u.quantita || 0), 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div data-testid="uscite-page" className="w-full space-y-6">
      {/* Header */}
      <div>
        <h1 className="font-heading text-2xl lg:text-3xl font-bold text-slate-900 tracking-tight">
          Uscite
        </h1>
        <p className="text-slate-500 text-sm mt-0.5">
          Seleziona una partita con giacenza per registrare un'uscita
        </p>
      </div>

      {/* Summary Card */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <Card className="kpi-card border-l-red-500">
          <CardContent className="p-4 lg:p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">
                  Totale Uscite Registrate
                </p>
                <p className="font-heading text-2xl font-bold text-slate-900 mt-1">
                  {formatNumber(totaleUscite)} kg
                </p>
                <p className="text-xs text-slate-500 mt-0.5">
                  {uscite.length} movimenti
                </p>
              </div>
              <div className="w-12 h-12 rounded-lg bg-red-100 flex items-center justify-center">
                <ArrowUpFromLine className="w-6 h-6 text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Partite in Giacenza */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
      >
        <Card className="card-industrial overflow-hidden">
          <CardHeader className="pb-3 px-4 lg:px-6">
            <CardTitle className="font-heading text-lg font-bold text-slate-900 flex items-center gap-2">
              <Package className="w-5 h-5 text-orange-500" />
              Partite in Giacenza ({filteredPartite.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {/* Search */}
            <div className="px-4 lg:px-6 pb-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  data-testid="search-giacenza-input"
                  placeholder="Cerca per codice, fornitore o prodotto..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 rounded-sm"
                />
              </div>
            </div>

            {filteredPartite.length > 0 ? (
              <div className="table-scroll-wrapper">
                <div className="rounded-sm border border-slate-200 overflow-hidden">
                  <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="font-heading font-bold text-xs uppercase tracking-wider text-slate-500">
                        Codice
                      </TableHead>
                      <TableHead className="font-heading font-bold text-xs uppercase tracking-wider text-slate-500">
                        Prodotto
                      </TableHead>
                      <TableHead className="font-heading font-bold text-xs uppercase tracking-wider text-slate-500">
                        Fornitore
                      </TableHead>
                      <TableHead className="font-heading font-bold text-xs uppercase tracking-wider text-slate-500">
                        Data Entrata
                      </TableHead>
                      <TableHead className="font-heading font-bold text-xs uppercase tracking-wider text-slate-500 text-right">
                        Giacenza
                      </TableHead>
                      <TableHead className="font-heading font-bold text-xs uppercase tracking-wider text-slate-500 text-center">
                        Azione
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredPartite.map((partita, index) => (
                      <TableRow 
                        key={partita.id}
                        data-testid={`giacenza-row-${index}`}
                        className="hover:bg-slate-50/50 cursor-pointer"
                      >
                        <TableCell className="font-mono text-sm font-bold text-slate-900">
                          {partita.codice_partita}
                        </TableCell>
                        <TableCell className="text-sm text-slate-700">
                          {partita.prodotto}
                        </TableCell>
                        <TableCell className="text-sm text-slate-700">
                          {partita.fornitore}
                        </TableCell>
                        <TableCell className="font-mono text-sm text-slate-600">
                          {formatDate(partita.data_entrata)}
                        </TableCell>
                        <TableCell className="font-mono text-sm font-bold text-emerald-600 text-right">
                          {formatNumber(partita.quantita_disponibile)} kg
                        </TableCell>
                        <TableCell className="text-center">
                          <Button
                            size="sm"
                            data-testid={`registra-uscita-${index}`}
                            onClick={() => handleOpenUscitaDialog(partita)}
                            className="btn-accent rounded-sm"
                          >
                            <Plus className="w-4 h-4 mr-1" />
                            Uscita
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
            ) : (
              <div className="text-center py-12 text-slate-400">
                <Package className="w-16 h-16 mx-auto mb-4 opacity-30" />
                <p className="text-lg font-medium">Nessuna partita in giacenza</p>
                <p className="text-sm mt-1">Registra nuove entrate per avere prodotti disponibili</p>
              </div>
            )}
          </CardContent>
        </Card>
      </motion.div>

      {/* Storico Uscite */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
      >
        <Card className="card-industrial">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="font-heading text-xl font-bold text-slate-900 flex items-center gap-2">
                <ArrowUpFromLine className="w-5 h-5 text-red-500" />
                Storico Uscite ({uscite.length})
              </CardTitle>
              {selectedIds.length > 0 && (
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => setBulkDeleteConfirm(true)}
                  className="h-8"
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  Elimina {selectedIds.length}
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {uscite.length > 0 ? (
              <div className="table-scroll-wrapper">
                <div className="rounded-sm border border-slate-200 overflow-hidden">
                  <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="w-10 text-center">
                        <Checkbox
                          checked={selectedIds.length === uscite.length && uscite.length > 0}
                          onCheckedChange={toggleSelectAll}
                        />
                      </TableHead>
                      <TableHead className="font-heading font-bold text-xs uppercase tracking-wider text-slate-500">
                        Codice Partita
                      </TableHead>
                      <TableHead className="font-heading font-bold text-xs uppercase tracking-wider text-slate-500">
                        Data
                      </TableHead>
                      <TableHead className="font-heading font-bold text-xs uppercase tracking-wider text-slate-500">
                        Cliente
                      </TableHead>
                      <TableHead className="font-heading font-bold text-xs uppercase tracking-wider text-slate-500 text-right">
                        Quantità
                      </TableHead>
                      <TableHead className="font-heading font-bold text-xs uppercase tracking-wider text-slate-500 text-center">
                        Azioni
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {uscite.map((uscita, index) => {
                      const isScarto = uscita.cliente?.toUpperCase() === "SCARTO";
                      return (
                      <TableRow 
                        key={uscita.id || index}
                        data-testid={`uscita-row-${index}`}
                        className={`${selectedIds.includes(uscita.id) ? 'bg-orange-50' : ''} ${isScarto 
                          ? "bg-amber-50 hover:bg-amber-100 border-l-4 border-l-amber-500" 
                          : "hover:bg-slate-50/50"
                        }`}
                      >
                        <TableCell className="text-center">
                          <Checkbox
                            checked={selectedIds.includes(uscita.id)}
                            onCheckedChange={() => toggleSelect(uscita.id)}
                          />
                        </TableCell>
                        <TableCell className="font-mono text-sm font-medium text-slate-900">
                          {uscita.codice_partita}
                        </TableCell>
                        <TableCell className="font-mono text-sm text-slate-600">
                          {formatDate(uscita.data)}
                        </TableCell>
                        <TableCell className={`text-sm ${isScarto ? "font-bold text-amber-700" : "text-slate-700"}`}>
                          {uscita.cliente}
                          {isScarto && <span className="ml-2 text-xs bg-amber-200 text-amber-800 px-1.5 py-0.5 rounded">SCARTO</span>}
                        </TableCell>
                        <TableCell className={`font-mono text-sm font-bold text-right ${isScarto ? "text-amber-700" : "text-red-600"}`}>
                          -{formatNumber(uscita.quantita)} kg
                        </TableCell>
                        <TableCell className="text-center">
                          <div className="flex items-center justify-center gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              data-testid={`edit-uscita-${index}`}
                              onClick={() => handleEditUscita(uscita)}
                              className="h-8 w-8 p-0"
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              data-testid={`delete-uscita-${index}`}
                              onClick={() => setDeleteConfirm(uscita)}
                              className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </div>
            ) : (
              <div className="text-center py-12 text-slate-400">
                <ArrowUpFromLine className="w-16 h-16 mx-auto mb-4 opacity-30" />
                <p className="text-lg font-medium">Nessuna uscita registrata</p>
              </div>
            )}
          </CardContent>
        </Card>
      </motion.div>

      {/* Dialog Nuova Uscita */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold flex items-center gap-2">
              <ArrowUpFromLine className="w-5 h-5 text-orange-500" />
              Registra Uscita
            </DialogTitle>
          </DialogHeader>
          
          {selectedPartita && (
            <div className="bg-slate-50 p-4 rounded-sm mb-4">
              <p className="text-sm text-slate-500">Partita selezionata</p>
              <p className="font-mono font-bold text-slate-900">{selectedPartita.codice_partita}</p>
              <p className="text-sm text-slate-600">{selectedPartita.prodotto}</p>
              <p className="text-sm text-emerald-600 font-bold mt-1">
                Disponibile: {formatNumber(selectedPartita.quantita_disponibile)} kg
              </p>
            </div>
          )}
          
          <form onSubmit={handleSubmitUscita} className="space-y-4">
            <div className="space-y-2">
              <Label className="font-heading font-bold text-slate-700">Cliente *</Label>
              <Select
                value={formData.cliente}
                onValueChange={(value) => setFormData({ ...formData, cliente: value })}
              >
                <SelectTrigger data-testid="cliente-select" className="rounded-sm">
                  <SelectValue placeholder="Seleziona cliente" />
                </SelectTrigger>
                <SelectContent>
                  {clienti.map((c) => (
                    <SelectItem key={c.id} value={c.nome}>
                      {c.nome}
                    </SelectItem>
                  ))}
                  <SelectItem value="__new__">+ Nuovo Cliente</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {formData.cliente === "__new__" && (
              <div className="space-y-2">
                <Label className="font-heading font-bold text-slate-700">Nome Nuovo Cliente *</Label>
                <Input
                  data-testid="nuovo-cliente-input"
                  value={formData.nuovoCliente}
                  onChange={(e) => setFormData({ ...formData, nuovoCliente: e.target.value })}
                  placeholder="Es: Azienda Verdi S.r.l."
                  className="rounded-sm"
                />
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="font-heading font-bold text-slate-700">Quantità (kg) *</Label>
                <Input
                  data-testid="quantita-uscita-input"
                  type="number"
                  step="0.01"
                  min="0"
                  max={selectedPartita?.quantita_disponibile || 0}
                  value={formData.quantita}
                  onChange={(e) => setFormData({ ...formData, quantita: e.target.value })}
                  placeholder="0.00"
                  className="rounded-sm font-mono"
                />
              </div>
              <div className="space-y-2">
                <Label className="font-heading font-bold text-slate-700">Data *</Label>
                <Input
                  data-testid="data-uscita-input"
                  type="date"
                  value={formData.data}
                  onChange={(e) => setFormData({ ...formData, data: e.target.value })}
                  className="rounded-sm font-mono"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="font-heading font-bold text-slate-700">Note</Label>
              <Textarea
                data-testid="note-uscita-input"
                value={formData.note}
                onChange={(e) => setFormData({ ...formData, note: e.target.value })}
                placeholder="Note aggiuntive..."
                className="rounded-sm"
                rows={2}
              />
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="rounded-sm">
                Annulla
              </Button>
              <Button type="submit" data-testid="submit-uscita-btn" className="btn-primary rounded-sm">
                Registra Uscita
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog Modifica Uscita */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="sm:max-w-[450px]">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold flex items-center gap-2">
              <Edit className="w-5 h-5 text-orange-500" />
              Modifica Uscita
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmitEdit} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label className="font-heading font-bold text-slate-700">Cliente</Label>
              <Input
                data-testid="edit-cliente-input"
                value={editFormData.cliente}
                onChange={(e) => setEditFormData({ ...editFormData, cliente: e.target.value })}
                className="rounded-sm"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="font-heading font-bold text-slate-700">Quantità (kg) *</Label>
                <Input
                  data-testid="edit-quantita-input"
                  type="number"
                  step="0.01"
                  min="0"
                  value={editFormData.quantita}
                  onChange={(e) => setEditFormData({ ...editFormData, quantita: e.target.value })}
                  className="rounded-sm font-mono"
                />
              </div>
              <div className="space-y-2">
                <Label className="font-heading font-bold text-slate-700">Data</Label>
                <Input
                  data-testid="edit-data-input"
                  type="date"
                  value={editFormData.data}
                  onChange={(e) => setEditFormData({ ...editFormData, data: e.target.value })}
                  className="rounded-sm font-mono"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="font-heading font-bold text-slate-700">Note</Label>
              <Textarea
                data-testid="edit-note-input"
                value={editFormData.note}
                onChange={(e) => setEditFormData({ ...editFormData, note: e.target.value })}
                className="rounded-sm"
                rows={2}
              />
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)} className="rounded-sm">
                Annulla
              </Button>
              <Button type="submit" data-testid="save-edit-btn" className="btn-primary rounded-sm">
                Salva Modifiche
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Conferma Eliminazione</AlertDialogTitle>
            <AlertDialogDescription>
              Sei sicuro di voler eliminare questa uscita?
              <br />
              La quantità verrà ripristinata nella partita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Annulla</AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-delete-uscita-btn"
              onClick={() => handleDeleteUscita(deleteConfirm?.id)}
              className="bg-red-600 hover:bg-red-700 rounded-sm"
            >
              Elimina
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk Delete Confirmation */}
      <AlertDialog open={bulkDeleteConfirm} onOpenChange={setBulkDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading text-red-600">Eliminazione Multipla</AlertDialogTitle>
            <AlertDialogDescription>
              Stai per eliminare <span className="font-bold">{selectedIds.length} uscite</span>.
              Le quantità verranno ripristinate nelle rispettive partite.
              <br /><br />
              <span className="text-red-600 font-medium">Questa azione non può essere annullata!</span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Annulla</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleBulkDelete}
              className="bg-red-600 hover:bg-red-700 rounded-sm"
            >
              Elimina {selectedIds.length} uscite
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
